/**
 * Postür Analizi tab.
 *
 * Mirrors PhotosTab's shape: a list of dated sessions, each with up to three
 * angle photos. Every photo is optional, like every photo everywhere in this
 * product — a session with only a front shot still saves.
 */

import { useState } from 'react';
import {
  formatDateLong,
  POSTURE_ANGLES,
  POSTURE_NOTE_MAX_LENGTH,
  type PostureAngle,
} from '@pp/shared';
import { PostureOverlay } from '@pp/report-template';
import { ApiError, api, request } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useApiData } from '../hooks/useApiData.js';
import { ImageError, optimizeImage } from '../lib/image.js';
import { useSession } from '../session/SessionContext.js';
import './PostureTab.css';

interface PosturePhoto {
  id: string;
  angle: PostureAngle;
  url: string;
  widthPx: number | null;
  heightPx: number | null;
  linePositionPercent: number | null;
}

interface PostureAssessment {
  id: string;
  assessedAt: string;
  score: number | null;
  note: string | null;
  photos: PosturePhoto[];
}

const ANGLE_LABEL: Record<PostureAngle, string> = {
  front: 'Ön',
  side: 'Yan',
  back: 'Arka',
};

const SCORES = [1, 2, 3, 4, 5] as const;

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function PostureTab({ studentId }: { studentId: string }) {
  const { data, error, loading, reload } = useApiData<{ items: PostureAssessment[] }>(
    `/api/students/${studentId}/posture`,
  );
  const { hasAdminAccess } = useSession();

  const [adding, setAdding] = useState(false);
  const [assessedAt, setAssessedAt] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState<PostureAssessment | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);

  const items = data?.items ?? [];

  async function createSession(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(undefined);
    try {
      await api.post(`/api/students/${studentId}/posture`, { assessedAt });
      setAdding(false);
      setAssessedAt(todayIso());
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(session: PostureAssessment) {
    try {
      await request(`/api/students/${studentId}/posture/${session.id}`, {
        method: 'DELETE',
      });
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  return (
    <div className="posture">
      <div className="posture__head">
        <h2 className="profile__section">Postür Analizi</h2>
        {!adding && (
          <Button type="button" onClick={() => setAdding(true)}>
            Yeni Analiz
          </Button>
        )}
      </div>

      {error && <p role="alert">{error}</p>}
      {formError && (
        <p className="posture__error" role="alert">
          {formError}
        </p>
      )}

      {adding && (
        <form onSubmit={createSession} className="posture__form" noValidate>
          <label className="posture__field">
            <span>Tarih</span>
            <input
              type="date"
              value={assessedAt}
              max={todayIso()}
              onChange={(e) => setAssessedAt(e.target.value)}
              required
            />
          </label>
          <div className="posture__actions">
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Vazgeç
            </Button>
            <Button type="submit" loading={busy}>
              Oluştur
            </Button>
          </div>
        </form>
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Henüz postür analizi yok.</p>
          <Button type="button" onClick={() => setAdding(true)}>
            Yeni Analiz
          </Button>
        </div>
      )}

      <ul className="posture__list">
        {items.map((session) => (
          <li key={session.id} className="posture__session">
            <PostureSession
              studentId={studentId}
              session={session}
              onChanged={reload}
              onDelete={() => setConfirmDelete(session)}
            />
          </li>
        ))}
      </ul>

      {confirmDelete && (
        <ConfirmDialog
          title="Postür analizi silinsin mi?"
          body={`${formatDateLong(confirmDelete.assessedAt)} tarihli analiz ve fotoğrafları kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
          confirmLabel="Sil"
          destructive
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const session = confirmDelete;
            setConfirmDelete(null);
            withAdmin(() => void remove(session));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Postür analizini kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </div>
  );
}

function PostureSession({
  studentId,
  session,
  onChanged,
  onDelete,
}: {
  studentId: string;
  session: PostureAssessment;
  onChanged: () => void;
  onDelete: () => void;
}) {
  const [score, setScore] = useState(session.score);
  const [note, setNote] = useState(session.note ?? '');
  const [savingMeta, setSavingMeta] = useState(false);

  async function saveMeta(next: { score?: number | null; note?: string }) {
    setSavingMeta(true);
    try {
      await api.patch(`/api/students/${studentId}/posture/${session.id}`, {
        score: next.score !== undefined ? next.score : score,
        note: next.note !== undefined ? next.note : note,
      });
      onChanged();
    } finally {
      setSavingMeta(false);
    }
  }

  return (
    <div className="posture__card">
      <div className="posture__card-head">
        <span className="posture__date">{formatDateLong(session.assessedAt)}</span>
        <button type="button" className="posture__delete" onClick={onDelete}>
          Sil
        </button>
      </div>

      <div className="posture__photos">
        {POSTURE_ANGLES.map((angle) => (
          <PostureAngleSlot
            key={angle}
            studentId={studentId}
            assessmentId={session.id}
            angle={angle}
            photo={session.photos.find((p) => p.angle === angle) ?? null}
            onChanged={onChanged}
          />
        ))}
      </div>

      <div className="posture__meta">
        <fieldset className="posture__score">
          <legend>Genel Postür</legend>
          <div className="posture__segments">
            {SCORES.map((value) => (
              <label
                key={value}
                className={score === value ? 'segment segment--on' : 'segment'}
              >
                <input
                  type="radio"
                  name={`${session.id}-score`}
                  checked={score === value}
                  onChange={() => {
                    setScore(value);
                    void saveMeta({ score: value });
                  }}
                  className="pp-visually-hidden"
                />
                {value}
              </label>
            ))}
            <button
              type="button"
              className="assess__clear"
              disabled={score === null || savingMeta}
              onClick={() => {
                setScore(null);
                void saveMeta({ score: null });
              }}
            >
              Temizle
            </button>
          </div>
        </fieldset>

        <label className="posture__field">
          <span>Not</span>
          <textarea
            rows={2}
            maxLength={POSTURE_NOTE_MAX_LENGTH}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => void saveMeta({ note })}
          />
        </label>
      </div>
    </div>
  );
}

function PostureAngleSlot({
  studentId,
  assessmentId,
  angle,
  photo,
  onChanged,
}: {
  studentId: string;
  assessmentId: string;
  angle: PostureAngle;
  photo: PosturePhoto | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function upload(file: File) {
    setBusy(true);
    setError(undefined);
    try {
      const optimized = await optimizeImage(file);
      const form = new FormData();
      form.append('image', optimized.file);
      form.append('angle', angle);
      form.append('widthPx', String(optimized.width));
      form.append('heightPx', String(optimized.height));

      await request(`/api/students/${studentId}/posture/${assessmentId}/photos`, {
        method: 'POST',
        formData: form,
      });
      onChanged();
    } catch (err) {
      if (err instanceof ImageError) setError(messageFor(err.code));
      else if (err instanceof ApiError) setError(messageFor(err.code));
      else setError(messageFor('INTERNAL_ERROR'));
    } finally {
      setBusy(false);
    }
  }

  async function setLine(percent: number) {
    if (!photo) return;
    try {
      await api.patch(
        `/api/students/${studentId}/posture/${assessmentId}/photos/${photo.id}`,
        { linePositionPercent: percent },
      );
      onChanged();
    } catch {
      // A failed drag write is low-stakes and retried on the next drag; no
      // error surface needed for this one interaction.
    }
  }

  async function removePhoto() {
    if (!photo) return;
    setBusy(true);
    try {
      await request(
        `/api/students/${studentId}/posture/${assessmentId}/photos/${photo.id}`,
        { method: 'DELETE' },
      );
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="posture__slot">
      <span className="posture__slot-label">{ANGLE_LABEL[angle]}</span>
      {photo ? (
        <div className="posture__slot-frame">
          <img src={photo.url} alt={ANGLE_LABEL[angle]} />
          <div className="posture__slot-overlay">
            <PostureOverlay
              linePositionPercent={photo.linePositionPercent}
              draggable
              onChange={setLine}
            />
          </div>
          <button
            type="button"
            className="posture__slot-remove"
            onClick={() => void removePhoto()}
            disabled={busy}
          >
            Kaldır
          </button>
        </div>
      ) : (
        <label className="posture__slot-empty">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
            disabled={busy}
          />
          <span>{busy ? 'Yükleniyor…' : 'Fotoğraf Yükle'}</span>
        </label>
      )}
      {error && (
        <p className="posture__slot-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
